export type AnswerHydrationLane = 'visible' | 'embed'

type QueueState = {
    queuedIds: Set<string>
    pendingIds: Set<string>
    timer: ReturnType<typeof setTimeout> | null
}

type AnswerHydrationQueueDependencies = {
    getAnswers: (answerIds: string[]) => Promise<unknown>
    getCachedAnswer: (answerId: string) => unknown
    isHydratedAnswerCacheEntry: (answer: unknown) => boolean
}

type AnswerHydrationQueueOptions = {
    delayMs?: number
    maxBatchSize?: number
}

const DEFAULT_DELAY_MS = 16
const DEFAULT_MAX_BATCH_SIZE = 100

export function createAnswerHydrationQueue(
    dependencies: AnswerHydrationQueueDependencies,
    options: AnswerHydrationQueueOptions = {},
) {
    const delayMs = options.delayMs ?? DEFAULT_DELAY_MS
    const maxBatchSize = options.maxBatchSize ?? DEFAULT_MAX_BATCH_SIZE

    const laneQueues: Record<AnswerHydrationLane, QueueState> = {
        visible: {
            queuedIds: new Set(),
            pendingIds: new Set(),
            timer: null,
        },
        embed: {
            queuedIds: new Set(),
            pendingIds: new Set(),
            timer: null,
        },
    }

    function flushAnswerHydrationLane(lane: AnswerHydrationLane) {
        const queue = laneQueues[lane]
        queue.timer = null

        const ids = Array.from(queue.queuedIds)
        queue.queuedIds.clear()
        if (ids.length === 0) return

        for (let index = 0; index < ids.length; index += maxBatchSize) {
            const batchIds = ids.slice(index, index + maxBatchSize)
            batchIds.forEach(id => queue.pendingIds.add(id))

            dependencies
                .getAnswers(batchIds)
                .catch(() => {})
                .finally(() => {
                    batchIds.forEach(id => queue.pendingIds.delete(id))
                })
        }
    }

    function queueAnswerHydration(
        answerIds: Array<string | number | null | undefined> | string | number | null | undefined,
        queueOptions: { lane?: AnswerHydrationLane; delayMs?: number } = {},
    ) {
        const lane = queueOptions.lane || 'visible'
        const queue = laneQueues[lane]
        const ids = (Array.isArray(answerIds) ? answerIds : [answerIds])
            .filter(Boolean)
            .map(id => String(id))

        ids.forEach(id => {
            if (queue.queuedIds.has(id) || queue.pendingIds.has(id)) return
            if (dependencies.isHydratedAnswerCacheEntry(dependencies.getCachedAnswer(id))) return

            queue.queuedIds.add(id)
        })

        if (queue.queuedIds.size === 0 || queue.timer) return

        queue.timer = setTimeout(
            () => flushAnswerHydrationLane(lane),
            queueOptions.delayMs ?? delayMs,
        )
    }

    function reset() {
        Object.values(laneQueues).forEach(queue => {
            if (queue.timer) clearTimeout(queue.timer)
            queue.timer = null
            queue.queuedIds.clear()
            queue.pendingIds.clear()
        })
    }

    return {
        queueAnswerHydration,
        reset,
    }
}
